<template>
  <v-card outlined class="proposal-card mb-2" :class="statusClass">
    <v-card-text class="py-3">
      <div class="d-flex align-center mb-1">
        <v-icon small :color="statusColor" class="mr-2">{{ statusIcon }}</v-icon>
        <span class="proposal-title">Proposta de registro</span>
        <v-spacer></v-spacer>
        <v-chip x-small :color="statusColor" text-color="white" label>
          {{ statusLabel }}
        </v-chip>
      </div>

      <!-- Preview / modo leitura -->
      <div v-if="!editing">
        <!-- Ingestão de laudo (T2.5): card rico com os parâmetros extraídos, pra o
             médico revisar o que será importado antes de aprovar. -->
        <div v-if="isExamIngest" class="exam-ingest">
          <div class="proposal-preview">{{ local.preview || defaultPreview }}</div>
          <div v-if="examMeta" class="exam-ingest__meta mt-1">{{ examMeta }}</div>

          <div v-if="examResults.length" class="exam-ingest__params mt-2">
            <div
              v-for="(p, i) in examResults"
              :key="i"
              class="exam-param"
            >
              <span class="exam-param__name">{{ p.tipo_analise }}</span>
              <span class="exam-param__val">
                {{ p.resultado }}<template v-if="p.unidade"> {{ p.unidade }}</template>
              </span>
              <span v-if="p.referencia" class="exam-param__ref">
                Ref.: {{ p.referencia }}
              </span>
            </div>
          </div>

          <div class="exam-ingest__file mt-2">
            <v-icon x-small color="grey darken-1" class="mr-1">mdi-paperclip</v-icon>
            Arquivo original preservado
          </div>
        </div>

        <div v-else class="proposal-preview">
          {{ local.preview || defaultPreview }}
        </div>
      </div>

      <!-- Modo edição -->
      <div v-else class="mt-2">
        <v-select
          v-if="isNote"
          v-model="editKind"
          :items="noteKinds"
          label="Tipo da nota"
          dense
          outlined
          hide-details="auto"
          class="mb-2"
        ></v-select>
        <v-textarea
          v-if="isNote"
          v-model="editValue"
          label="Conteúdo da nota"
          rows="3"
          auto-grow
          dense
          outlined
          hide-details="auto"
          class="mb-2"
        ></v-textarea>
        <v-text-field
          v-else
          v-model="editValue"
          label="Valor"
          dense
          outlined
          hide-details="auto"
          class="mb-2"
        ></v-text-field>
        <v-text-field
          v-model="editPreview"
          label="Descrição (como aparece no card)"
          dense
          outlined
          hide-details="auto"
        ></v-text-field>
      </div>
    </v-card-text>

    <v-divider v-if="isActionable"></v-divider>

    <v-card-actions v-if="isActionable" class="py-2 px-3">
      <template v-if="!editing">
        <v-btn
          small
          color="success"
          :loading="busy === 'approve'"
          :disabled="!!busy"
          @click="onApprove"
        >
          <v-icon left small>mdi-check</v-icon> Aprovar
        </v-btn>
        <v-btn
          v-if="!isExamIngest"
          small
          text
          color="primary"
          :disabled="!!busy"
          @click="startEdit"
        >
          <v-icon left small>mdi-pencil</v-icon> Editar
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          small
          text
          color="error"
          :loading="busy === 'reject'"
          :disabled="!!busy"
          @click="onReject"
        >
          <v-icon left small>mdi-close</v-icon> Rejeitar
        </v-btn>
      </template>

      <template v-else>
        <v-btn
          small
          color="primary"
          :loading="busy === 'edit'"
          :disabled="!!busy"
          @click="onSaveEdit"
        >
          <v-icon left small>mdi-content-save</v-icon> Salvar
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn small text :disabled="!!busy" @click="cancelEdit">Cancelar</v-btn>
      </template>
    </v-card-actions>

    <!-- Confirmação extra para ações sensíveis (tier 2, ex.: parar medicamento). -->
    <v-dialog v-model="confirmDialog" max-width="400" persistent scrollable>
      <v-card>
        <v-card-title class="text-subtitle-1">
          <v-icon left color="warning">mdi-alert</v-icon>
          Confirmar ação sensível
        </v-card-title>
        <!-- Filete laranja: é um aviso, mesmo com o cabeçalho no verde padrão. -->
        <v-card-text class="ds-dialog-body">
          <section class="ds-form-section ds-form-section--warning">
            Esta ação tem impacto clínico e não é automática. Deseja aprovar?
            <div class="mt-2 font-weight-medium">
              {{ local.preview || defaultPreview }}
            </div>
          </section>
        </v-card-text>
        <v-card-actions class="ds-dialog-actions">
          <v-btn
            outlined
            color="primary"
            :disabled="busy === 'approve'"
            @click="confirmDialog = false"
          >
            Cancelar
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="success"
            depressed
            :loading="busy === 'approve'"
            @click="confirmApprove"
          >
            Confirmar e aprovar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import { useSnackbar } from "@/composables/useSnackbar";
import {
  approveProposal,
  rejectProposal,
  editProposal,
} from "@/services/assistantService";

/**
 * Card de uma Proposta da salvIA (Salvia Agêntico T2.6).
 *
 * Mostra o preview e permite Aprovar / Editar / Rejeitar. Faz as chamadas ao gate
 * de Propostas e emite `update` com a proposta atualizada (o pai substitui no
 * estado). Aprovar EXECUTA a escrita real no Laravel — nada é gravado antes disso.
 */
export default {
  name: "ProposalCard",
  props: {
    proposal: { type: Object, required: true },
  },
  setup() {
    const { success, error, erroDeRequisicao } = useSnackbar();
    return { success, error, erroDeRequisicao };
  },
  data() {
    return {
      local: { ...this.proposal },
      editing: false,
      editValue: "",
      editPreview: "",
      editKind: "general",
      confirmDialog: false, // diálogo de confirmação de ação sensível (tier 2)
      // Rótulos alinhados ao EditClinicalNotesDialog do prontuário.
      noteKinds: [
        { value: "life_history", text: "Histórico de vida" },
        { value: "general", text: "Geral" },
        { value: "other", text: "Outro" },
      ],
      busy: null, // 'approve' | 'reject' | 'edit' | null
    };
  },
  computed: {
    isActionable() {
      return ["pending", "edited"].includes(this.local.status);
    },
    // Nota clínica usa payload estruturado (data.content + data.kind) em vez de
    // `value`; a edição precisa tratar conteúdo/tipo, não um valor simples.
    isNote() {
      return (this.local.payload || {}).field_key === "clinical_note";
    },
    // Ação sensível (tier 2): aprovar passa por um diálogo de confirmação extra.
    isSensitive() {
      return Number(this.local.tier) >= 2;
    },
    // Ingestão de laudo (T2.5): preview informativo, sem edição inline por ora.
    isExamIngest() {
      return this.local.action_type === "exam_ingest";
    },
    // Dados do laudo extraído (payload.exam), montados pelo backend em
    // /api/proposals/from-exam-pdf. Base do card rico de ingestão.
    examData() {
      return (this.local.payload || {}).exam || {};
    },
    examResults() {
      return Array.isArray(this.examData.results) ? this.examData.results : [];
    },
    // Linha de contexto do laudo: "<exame> · <lab> · <data>", só o que existir.
    examMeta() {
      return [this.examData.exam_name, this.examData.lab_name, this.examData.date]
        .filter(Boolean)
        .join(" · ");
    },
    defaultPreview() {
      const p = this.local.payload || {};
      const val = p.data?.content ?? p.value;
      return val ? `Registrar ${p.field_key || "dado"}: ${val}` : "Registro clínico";
    },
    statusLabel() {
      return (
        {
          pending: "Pendente",
          edited: "Editada",
          executed: "Aprovada",
          rejected: "Rejeitada",
          failed: "Falhou",
          approved: "Processando",
        }[this.local.status] || this.local.status
      );
    },
    statusColor() {
      return (
        {
          pending: "amber darken-2",
          edited: "amber darken-2",
          executed: "success",
          rejected: "grey",
          failed: "error",
          approved: "primary",
        }[this.local.status] || "grey"
      );
    },
    statusIcon() {
      return (
        {
          pending: "mdi-clipboard-text-clock",
          edited: "mdi-clipboard-edit",
          executed: "mdi-clipboard-check",
          rejected: "mdi-clipboard-remove",
          failed: "mdi-alert-circle",
        }[this.local.status] || "mdi-clipboard-text"
      );
    },
    statusClass() {
      return `proposal-card--${this.local.status}`;
    },
  },
  watch: {
    proposal: {
      deep: true,
      handler(v) {
        this.local = { ...v };
      },
    },
  },
  methods: {
    startEdit() {
      const p = this.local.payload || {};
      if (this.isNote) {
        this.editValue = p.data?.content ?? p.value ?? "";
        this.editKind = p.data?.kind ?? "general";
      } else {
        this.editValue = p.value != null ? String(p.value) : "";
      }
      this.editPreview = this.local.preview || this.defaultPreview;
      this.editing = true;
    },
    cancelEdit() {
      this.editing = false;
    },
    // Ações sensíveis (tier 2) abrem o diálogo de confirmação antes de executar; as
    // demais aprovam direto.
    onApprove() {
      if (this.isSensitive) {
        this.confirmDialog = true;
        return;
      }
      this.doApprove(false);
    },
    confirmApprove() {
      this.doApprove(true);
    },
    async doApprove(confirm) {
      this.busy = "approve";
      try {
        const updated = await approveProposal(this.local.id, confirm);
        this.local = { ...this.local, ...updated };
        this.confirmDialog = false;
        this.$emit("update", this.local);
        this.success("Registro efetivado.");
      } catch (e) {
        const msg =
          e?.response?.data?.data?.error ||
          e?.response?.data?.message ||
          "Não foi possível aprovar a proposta.";
        // 422 = execução falhou: reflete o status vindo do backend, se houver.
        // 409 = requer confirmação (rede fora do fluxo normal): mantém acionável.
        const failed = e?.response?.data?.data;
        if (failed && e?.response?.status === 422) {
          this.local = { ...this.local, ...failed };
          this.$emit("update", this.local);
        }
        this.erroDeRequisicao(e, msg);
      } finally {
        this.busy = null;
      }
    },
    async onReject() {
      this.busy = "reject";
      try {
        const updated = await rejectProposal(this.local.id);
        this.local = { ...this.local, ...updated };
        this.$emit("update", this.local);
        this.success("Proposta rejeitada.");
      } catch (e) {
        this.erroDeRequisicao(e, 
          e?.response?.data?.message || "Não foi possível rejeitar a proposta.",
        );
      } finally {
        this.busy = null;
      }
    },
    async onSaveEdit() {
      this.busy = "edit";
      try {
        let payload;
        if (this.isNote) {
          // Reconstrói o payload estruturado da nota; remove `value` legado para
          // não competir com `data.content` no Router do Laravel.
          payload = { ...(this.local.payload || {}) };
          delete payload.value;
          payload.data = {
            ...(this.local.payload?.data || {}),
            content: this.editValue,
            kind: this.editKind,
          };
        } else {
          payload = { ...(this.local.payload || {}), value: this.editValue };
        }
        const updated = await editProposal(
          this.local.id,
          payload,
          this.editPreview,
        );
        this.local = { ...this.local, ...updated };
        this.editing = false;
        this.$emit("update", this.local);
        this.success("Proposta atualizada.");
      } catch (e) {
        this.erroDeRequisicao(e, 
          e?.response?.data?.message || "Não foi possível editar a proposta.",
        );
      } finally {
        this.busy = null;
      }
    },
  },
};
</script>

<style scoped>
.proposal-card {
  border-left: 4px solid var(--v-primary-base, #3e8e73);
  border-radius: 10px;
}
.proposal-card--executed {
  border-left-color: #4caf50;
}
.proposal-card--rejected {
  border-left-color: #9e9e9e;
  opacity: 0.85;
}
.proposal-card--failed {
  border-left-color: #ff5252;
}
.proposal-title {
  font-weight: 600;
  font-size: 0.85rem;
}
.proposal-preview {
  font-size: 0.95rem;
  color: rgba(0, 0, 0, 0.78);
}
.exam-ingest__meta {
  font-size: 0.8rem;
  color: rgba(0, 0, 0, 0.6);
}
.exam-ingest__params {
  max-height: 220px;
  overflow-y: auto;
  border: 1px solid #e6e8e8;
  border-radius: 8px;
}
.exam-param {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 2px 8px;
  padding: 6px 10px;
  font-size: 0.85rem;
}
.exam-param:not(:last-child) {
  border-bottom: 1px solid #f0f1f1;
}
.exam-param__name {
  font-weight: 600;
  flex: 1 1 40%;
}
.exam-param__val {
  color: rgba(0, 0, 0, 0.82);
}
.exam-param__ref {
  flex-basis: 100%;
  font-size: 0.78rem;
  color: rgba(0, 0, 0, 0.55);
}
.exam-ingest__file {
  font-size: 0.78rem;
  color: rgba(0, 0, 0, 0.55);
  display: flex;
  align-items: center;
}
</style>
